import { useState, useEffect, useRef } from 'react'
import { Html5Qrcode } from 'html5-qrcode'
import api from '../../utils/api'
import toast from 'react-hot-toast'
import { formatDateTime } from '../../utils/dateFormatter'
import '../QRScan.css'

const extractUuidFromPayload = (text) => {
  if (!text || typeof text !== 'string') return null
  const t = text.trim()
  try {
    const urlObj = new URL(t, window.location.origin)
    const pathParts = urlObj.pathname.split('/')
    const qrIndex = pathParts.indexOf('qr')
    if (qrIndex !== -1 && pathParts[qrIndex + 1]) {
      return pathParts[qrIndex + 1]
    }
  } catch {
    /* fall through */
  }
  const m = t.match(/\/qr\/([a-f0-9-]{36})/i)
  if (m) return m[1]
  const u = t.match(/^([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/i)
  if (u) return u[1]
  return null
}

/**
 * Admin-only: scan a NAH44 sticker QR to see database tag ID, UUID, last printed, etc.
 */
const AdminTagScanner = () => {
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState('')
  const [tag, setTag] = useState(null)
  const [loadingLookup, setLoadingLookup] = useState(false)
  const html5QrCodeRef = useRef(null)

  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        const instance = html5QrCodeRef.current
        html5QrCodeRef.current = null
        void (async () => {
          try {
            await instance.stop()
          } catch {
            /* */
          }
          try {
            await instance.clear()
          } catch {
            /* */
          }
        })()
      }
    }
  }, [])

  const lookupUuid = async (uuid) => {
    setLoadingLookup(true)
    setError('')
    setTag(null)
    try {
      const res = await api.get('/qrcodes/lookup/tag', { params: { uuid } })
      if (res.data?.success && res.data?.data?.tag) {
        setTag(res.data.data.tag)
        toast.success('Tag found')
      } else {
        setError('Unexpected response')
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Lookup failed'
      setError(msg)
      toast.error(msg)
    } finally {
      setLoadingLookup(false)
    }
  }

  const onDecoded = (decodedText) => {
    const uuid = extractUuidFromPayload(decodedText)
    if (!uuid) {
      setError('Not a NAH44 QR (expected URL with /qr/{uuid} or a raw UUID).')
      toast.error('Invalid QR for tag lookup')
      return
    }
    void stopScanning()
    void lookupUuid(uuid)
  }

  const stopScanning = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop()
        await html5QrCodeRef.current.clear()
      } catch {
        /* */
      }
      html5QrCodeRef.current = null
    }
    setScanning(false)
  }

  const startScanning = async () => {
    if (!window.isSecureContext) {
      setError('Use HTTPS or localhost for camera access.')
      return
    }
    setError('')
    setTag(null)

    if (html5QrCodeRef.current) {
      await stopScanning()
    }

    const el = document.getElementById('admin-qr-reader')
    if (!el) {
      setError('Scanner mount missing.')
      return
    }

    const html5QrCode = new Html5Qrcode('admin-qr-reader')
    html5QrCodeRef.current = html5QrCode

    const config = { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 }
    const onErr = (msg) => {
      if (!msg.includes('NotFoundException') && !msg.includes('No MultiFormat Readers')) {
        console.debug('scan:', msg)
      }
    }

    const tryStart = async (cam) => {
      await html5QrCode.start(cam, config, onDecoded, onErr)
    }

    try {
      try {
        await tryStart({ facingMode: 'environment' })
      } catch (e1) {
        await html5QrCode.clear().catch(() => {})
        try {
          await tryStart({ facingMode: 'user' })
        } catch (e2) {
          await html5QrCode.clear().catch(() => {})
          const cameras = await Html5Qrcode.getCameras()
          if (!cameras?.length) throw e2
          await tryStart(cameras[0].id)
        }
      }
      setScanning(true)
    } catch (err) {
      console.error(err)
      setError(err.message || 'Could not start camera')
      html5QrCodeRef.current = null
    }
  }

  const handleToggle = () => {
    if (scanning) {
      void stopScanning()
    } else {
      void startScanning()
    }
  }

  return (
    <div className="admin-qrcodes animate-fadeIn">
      <div className="qr-header-section" style={{ marginBottom: '1.5rem' }}>
        <div>
          <h1>Tag lookup (scan)</h1>
          <p className="admin-subtitle">
            Scan a sticker QR to see the database tag ID, UUID, last printed time, and assignment. Admin only.
          </p>
        </div>
      </div>

      <div className="card" style={{ maxWidth: '560px' }}>
        {error && (
          <div className="status-card status-error" style={{ marginBottom: '1rem' }}>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>{error}</p>
          </div>
        )}

        <div
          style={{
            position: 'relative',
            minHeight: '280px',
            border: scanning ? '2px solid var(--primary-color)' : '2px dashed var(--border-color)',
            borderRadius: '1rem',
            overflow: 'hidden',
            marginBottom: '1rem'
          }}
        >
          <div id="admin-qr-reader" style={{ width: '100%', minHeight: '280px' }} />
          {!scanning && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                color: 'var(--text-secondary)',
                fontSize: '0.9rem',
                textAlign: 'center',
                padding: '1rem'
              }}
            >
              Camera preview appears here
            </div>
          )}
        </div>

        <button type="button" className="btn-primary" onClick={handleToggle} style={{ width: '100%', marginBottom: '0.75rem' }}>
          {scanning ? 'Stop camera' : 'Scan sticker QR'}
        </button>

        {loadingLookup && <p style={{ color: 'var(--text-secondary)' }}>Looking up tag…</p>}

        {tag && (
          <div
            style={{
              marginTop: '1rem',
              padding: '1rem',
              background: 'var(--bg-input)',
              borderRadius: '0.75rem',
              border: '1px solid var(--border-color)',
              fontSize: '0.9rem'
            }}
          >
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Tag details</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '0.35rem 0', color: 'var(--text-secondary)', width: '40%' }}>Database ID</td>
                  <td style={{ fontWeight: 700 }}>{tag.id}</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.35rem 0', color: 'var(--text-secondary)' }}>UUID</td>
                  <td>
                    <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{tag.code_uuid}</code>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '0.35rem 0', color: 'var(--text-secondary)' }}>Type</td>
                  <td>{tag.is_car ? 'Car' : 'Bike'}</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.35rem 0', color: 'var(--text-secondary)' }}>Status</td>
                  <td>{tag.is_active ? 'Active' : 'Inactive'}</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.35rem 0', color: 'var(--text-secondary)' }}>Last printed</td>
                  <td>{tag.last_printed_at ? formatDateTime(tag.last_printed_at) : '—'}</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.35rem 0', color: 'var(--text-secondary)' }}>Franchise / owner</td>
                  <td>{tag.owner_business_name || tag.owner_name || (tag.owner_id ? `User #${tag.owner_id}` : '—')}</td>
                </tr>
                {tag.linking && (
                  <>
                    <tr>
                      <td style={{ padding: '0.35rem 0', color: 'var(--text-secondary)' }}>Vehicle</td>
                      <td>{tag.linking.vehicle_number || '—'}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.35rem 0', color: 'var(--text-secondary)' }}>Linked name</td>
                      <td>
                        {[tag.linking.first_name, tag.linking.last_name].filter(Boolean).join(' ') || '—'}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminTagScanner
